import { Box, Button } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useEffect, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { UI } from '../../../../../constants';
import { TLocalesKeys as K } from '../../../../../types';
import { logChatbotContactFlowQuickAccessAvailable } from '../../../../bi';
import { useIsAiExperience } from '../../../../common/hooks/useIsAiExperience';
import { useIsFirstPriorityExperience } from '../../../../common/hooks/useIsFirstPriorityExperience';
import { useUserAction } from '../../../../common/hooks/useUserAction';
import { createBotTyping } from '../../../../common/messageCreators';
import { connectingAgent } from '../../../../common/messageCreators/connectingAgent';
import { chatSlice } from '../../../../common/reducers/chat';
import { selectConversationId, selectIsConnectingAgent } from '../../../../common/selectors/chat';
import { selectErrors } from '../../../../common/selectors/errors';
import { selectFieldState } from '../../../../common/selectors/field';
import { store } from '../../../../common/store';
import { selectIsHidden } from '../selectors';

export const DirectSupportButton: FC = React.memo(() => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { disabled } = useSelector(selectFieldState);
  const errors = useSelector(selectErrors);
  const conversationId = useSelector(selectConversationId);
  const isConnectingAgent = useSelector(selectIsConnectingAgent);
  const isHidden = useSelector(selectIsHidden);
  const isFirstPriority = useIsFirstPriorityExperience();
  const isAiExperience = useIsAiExperience();
  const shouldLogDirectSupportButton = useRef(false);

  const buttonLabel = t<K>('direct_support_request_btn_label');
  const shouldShowDirectSupportButton =
    isFirstPriority &&
    isAiExperience &&
    !disabled &&
    !isHidden &&
    // LLM node can be shown in a process of connecting an expert, so avoiding showing the button if we're already in the process
    !isConnectingAgent &&
    !errors.length;

  const emitDirectSupport = useUserAction(UI.DIRECT_SUPPORT_REQUESTED);
  const directSupportHandler = useCallback(() => {
    emitDirectSupport({ label: buttonLabel });
    batch(() => {
      dispatch(chatSlice.actions.addItem(connectingAgent()));
      dispatch(chatSlice.actions.startConnectingAgent());
      dispatch(chatSlice.actions.addItem(createBotTyping({ rm: true })));
    });
  }, [dispatch, emitDirectSupport, buttonLabel]);

  useEffect(() => {
    if (!shouldLogDirectSupportButton.current) {
      shouldLogDirectSupportButton.current = true;
    }
  }, [conversationId]);

  useEffect(() => {
    if (shouldShowDirectSupportButton && shouldLogDirectSupportButton.current) {
      logChatbotContactFlowQuickAccessAvailable(store.getState());
    }
  }, [shouldShowDirectSupportButton]);

  if (!shouldShowDirectSupportButton) {
    return null;
  }

  return (
    <Box position="absolute" top="-30px">
      <Button priority="secondary" size="tiny" onClick={directSupportHandler}>
        {buttonLabel}
      </Button>
    </Box>
  );
});
